import { useContext, useEffect, useState } from 'react'
import useAppContextRef from '@/services/useAppContextRef'
import { AppContext, AppContextType } from '@/AppContext'
import { UserContext, UserContextType } from '@/UserContext'
import { RoomContext, RoomContextType } from '@/RoomContext'

const getSecondsRemaining = (sessionLimitEndTime: Date) => {
    const msRemaining = sessionLimitEndTime.getTime() - new Date().getTime()
    return Math.floor(msRemaining / 1000)
}

export default function useSessionLimitCountdownService() {
    const { appStateRef } = useAppContextRef()
    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { state } = useContext(UserContext) as UserContextType
    const { state: roomState } = useContext(RoomContext) as RoomContextType

    const [showTimer, set_showTimer] = useState<boolean>(false)
    const [remainingTimeS, set_remainingTimeS] = useState<number>(0)

    const SHOW_BOOST_POPUP_S = 5 * 60
    const SHOW_TIMER_S = 10 * 60

    const sessionState = roomState.sessionState

    const showTimeRunningOutPopup = (forEndTime: string) => {
        appDispatch({
            type: 'update_app_state',
            value: {
                showUpgradeToMuseProDialog: {
                    type: 'sessionDurationLimitRunningOut',
                },
                sessionDurationLimitRunningOutDialogShown: { forEndTime },
            },
        })
    }

    const showBoostPopupIfNot = () => {
        // show only if has not been shown before w/ key=`sessionLimitEndTimerStr`; this is
        // to prevent showing the dialog multiple times for the same end time when the component
        // reloads.
        if (
            appStateRef.current.appState.sessionDurationLimitRunningOutDialogShown?.forEndTime !==
            sessionState.sessionLimitEndTimeStr
        ) {
            showTimeRunningOutPopup(sessionState.sessionLimitEndTimeStr || '')
        }
    }

    useEffect(() => {
        let timer: NodeJS.Timeout | undefined = undefined

        const sessionLimitEndTime = sessionState.sessionLimitEndTime
        // const sessionLimitEndTime = new Date(new Date().getTime() + 9 * 60 * 1000) // add 9 minutes
        if (sessionLimitEndTime) {
            const runner1s = () => {
                const sRemaining = getSecondsRemaining(sessionLimitEndTime)
                set_remainingTimeS(sRemaining)

                if (sRemaining < SHOW_BOOST_POPUP_S) {
                    showBoostPopupIfNot()
                }

                if (sRemaining <= 0) {
                    clearInterval(timer)
                }
            }

            const runner30s = () => {
                const sRemaining = getSecondsRemaining(sessionLimitEndTime)
                set_remainingTimeS(sRemaining)

                // If 10 minutes remaining, then show timer and switch over to updating every second.
                if (sRemaining < SHOW_TIMER_S || sessionState.isBoosted) {
                    clearInterval(timer)
                    timer = setInterval(runner1s, 1000)

                    set_showTimer(true)
                } else {
                    set_showTimer(false)
                }
            }

            timer = setInterval(runner30s, 1000 * 30)
            runner30s()
        } else {
            set_showTimer(false)
        }

        return () => {
            clearInterval(timer)
        }
        // don't compare on `sessionState.sessionLimitEndTime` and instead use the string version
        // because sessionLimitEndTime is a date and hence an object.
    }, [sessionState.sessionLimitEndTimeStr])

    if (sessionState.sessionLimitEndTime == null) {
        return {
            showTimer: false,
            canBoost: false,
            remainingTimeText: '',
            remainingTimeS: null,
        }
    }

    const canBoost = remainingTimeS < SHOW_TIMER_S && !state.isGuest

    return {
        showTimer,
        canBoost,
        remainingTimeS: remainingTimeS ?? null,
    }
}
